import { useState, useCallback, useEffect, useRef, type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Button, Card, Badge, Input, FontFamilySelect, FONT_OPTIONS } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { Textarea } from '@/components/ui/Textarea'
import { FileUpload } from '@/components/ui/FileUpload'
import { useMicrosite, useMicrositePages, useUpdateMicrosite, usePublishMicrosite } from '@/hooks/useMicrosite'
import { MicrositePageList } from '@/pages/microsites/MicrositePageList'
import { api } from '@/lib/api'
import type { MicrositeTheme } from '@/hooks/useMicrosite'
import type { Property } from '@/types'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SLUG_REGEX = /^[a-z0-9-]+$/

const TEMPLATES = ['modern', 'classic', 'elegant'] as const
type Template = (typeof TEMPLATES)[number]

// Modern: Teal brand, clean shadows, 12px radius — inspired by peerapatresidence.com
// Classic: Warm brown, bordered sections, no gradients, serif feel
// Elegant: Royal purple + gold accents, restrained luxury — inspired by Thai Airways
const TEMPLATE_PRESETS: Record<Template, {
  theme: Partial<MicrositeTheme>
  btnR: string       // button border-radius
  btnBorder: (c: string) => string
  cardR: string      // card border-radius
  heroTextLight: boolean
}> = {
  modern: {
    btnR: '8px',
    btnBorder: () => 'none',
    cardR: '12px',
    heroTextLight: true,
    theme: {
      primary_color: '#056881',
      secondary_color: '#54595f',
      accent_color: '#e6f0f2',
      button_color: '#056881',
      button_text_color: '#ffffff',
      header_bg_color: '#ffffff',
      header_text_color: '#056881',
      footer_bg_color: '#11151b',
      footer_text_color: '#94a3b8',
      font_family: 'Bai Jamjuree',
      heading_font_family: 'Bai Jamjuree',
    },
  },
  classic: {
    btnR: '4px',
    btnBorder: (c) => `1.5px solid ${c}`,
    cardR: '4px',
    heroTextLight: false,
    theme: {
      primary_color: '#7c4a1e',
      secondary_color: '#6b5e50',
      accent_color: '#b8860b',
      button_color: '#7c4a1e',
      button_text_color: '#ffffff',
      header_bg_color: '#3b2f20',
      header_text_color: '#f5e6d3',
      footer_bg_color: '#3b2f20',
      footer_text_color: '#c4a882',
      font_family: 'Sarabun',
      heading_font_family: 'Bai Jamjuree',
    },
  },
  elegant: {
    btnR: '0px',
    btnBorder: () => 'none',
    cardR: '0px',
    heroTextLight: true,
    theme: {
      primary_color: '#370e62',
      secondary_color: '#54595f',
      accent_color: '#f5c300',
      button_color: '#370e62',
      button_text_color: '#ffffff',
      header_bg_color: '#370e62',
      header_text_color: '#ffffff',
      footer_bg_color: '#370e62',
      footer_text_color: '#a78bfa',
      font_family: 'Montserrat',
      heading_font_family: 'Montserrat',
    },
  },
}


// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------

function ColorPicker({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="form-control">
      <label className="label"><span className="label-text">{label}</span></label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          className="w-10 h-10 rounded cursor-pointer border border-base-300 p-0.5 bg-transparent"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
        <Input
          type="text"
          className="flex-1"
          size="sm"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          aria-label={`${label} hex value`}
          maxLength={7}
        />
      </div>
    </div>
  )
}

export type PreviewPage = 'landing' | 'rooms' | 'contact'
type PreviewDevice = 'desktop' | 'mobile'

const HERO_IMAGES: Record<Template, string> = {
  modern: '/images/microsite/hero-modern.jpg',
  classic: '/images/microsite/hero-classic.jpg',
  elegant: '/images/microsite/hero-elegant.jpg',
}

// Load Google Fonts dynamically
function useGoogleFonts(fonts: string[]) {
  useEffect(() => {
    // Inspection low: API-provided font names are injected into a <link> URL —
    // restrict to the same allowlist the pickers offer.
    const uniqueFonts = [...new Set(fonts.filter((f) => f && (FONT_OPTIONS as readonly string[]).includes(f)))]
    if (uniqueFonts.length === 0) return
    const id = 'microsite-google-fonts'
    let link = document.getElementById(id) as HTMLLinkElement | null
    const families = uniqueFonts.map((f) => f.replace(/\s+/g, '+')).join('&family=')
    const href = `https://fonts.googleapis.com/css2?family=${families}:wght@400;600;700&display=swap`
    if (link) {
      if (link.href !== href) link.href = href
    } else {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href = href
      document.head.appendChild(link)
    }
  }, [fonts])
}

// Dynamically inject template CSS for preview — only loads when microsite tab is active
function useMicrositeCSS(template: Template) {
  useEffect(() => {
    const id = `microsite-${template}-css`
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = `/microsite/${template}.css`
    document.head.appendChild(link)
  }, [template])
}

// ---------------------------------------------------------------------------
// ThemePreview sub-components (module-level to satisfy React Compiler)
// ---------------------------------------------------------------------------

interface PreviewHeaderProps {
  logoUrl: string | undefined
  propertyName: string
  nav: Record<string, string>
  navOpen: boolean
  setNavOpen: (open: boolean) => void
  previewPage: PreviewPage
}

function PreviewHeader({ logoUrl, propertyName, nav, navOpen, setNavOpen, previewPage }: PreviewHeaderProps) {
  const { t: tc } = useTranslation('common')
  return (
    <header className="site-header">
      <div className="container header-inner">
        <a href="#" className="site-logo" onClick={(e) => e.preventDefault()}>
          <img src={logoUrl || '/icon-clean.svg'} alt="" className="logo-image" />
          <span className="logo-text">{propertyName}</span>
        </a>
        <nav className="site-nav">
          {/* Hamburger — hidden by default, shown via @container query at ≤768px */}
          <button className="nav-toggle" aria-label={tc('labels.toggle_navigation')} onClick={() => setNavOpen(!navOpen)}>
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
            <span className="nav-toggle__bar" />
          </button>
          <ul className={`nav-list${navOpen ? ' is-open' : ''}`}>
            {(['landing', 'rooms', 'contact'] as const).map((k) => (
              <li key={k} className="nav-item">
                <span className={`nav-link${k === previewPage ? ' nav-link--active' : ''}`}>{nav[k]}</span>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </header>
  )
}

interface PreviewFooterProps {
  propertyName: string
  footerText: string | undefined
  contactPhone: string | undefined
  contactEmail: string | undefined
  nav: Record<string, string>
}

function PreviewFooter({ propertyName, footerText, contactPhone, contactEmail, nav }: PreviewFooterProps) {
  const { t } = useTranslation('microsite')
  return (
    <footer className="site-footer">
      <div className="container footer-inner">
        <div className="footer-col">
          <h3 className="footer-heading">{propertyName}</h3>
          <p className="footer-tagline">{footerText || `© ${new Date().getFullYear()} ${propertyName}`}</p>
        </div>
        <div className="footer-col">
          <h3 className="footer-heading">{t('preview.footer_contact')}</h3>
          <ul className="footer-contact-list">
            {contactPhone && (
              <li className="footer-contact-item">
                <span className="footer-contact-label">{t('preview.footer_phone')}: </span>
                <span className="footer-contact-link">{contactPhone}</span>
              </li>
            )}
            {contactEmail && (
              <li className="footer-contact-item">
                <span className="footer-contact-label">{t('preview.footer_email')}: </span>
                <span className="footer-contact-link">{contactEmail}</span>
              </li>
            )}
          </ul>
        </div>
        <div className="footer-col">
          <h3 className="footer-heading">{t('preview.footer_pages')}</h3>
          <ul className="footer-nav-list">
            {(['landing', 'rooms', 'contact'] as const).map((k) => (
              <li key={k} className="footer-nav-item">
                <span className="footer-nav-link">{nav[k]}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="footer-bottom">
        <div className="container">
          <p className="footer-copy">© {new Date().getFullYear()} {propertyName}. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

interface ContactDetailsProps {
  contactAddress: string | undefined
  contactPhone: string | undefined
  contactEmail: string | undefined
}

function ContactDetails({ contactAddress, contactPhone, contactEmail }: ContactDetailsProps) {
  const items = [
    contactAddress && { icon: '📍', text: contactAddress },
    contactPhone && { icon: '📞', text: contactPhone },
    contactEmail && { icon: '✉️', text: contactEmail },
  ].filter(Boolean) as { icon: string; text: string }[]
  if (items.length === 0) return null
  return (
    <div className="contact-details">
      {items.map((item) => (
        <div key={item.icon} className="contact-item">
          <span className="contact-icon">{item.icon}</span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  )
}

function MapBox({ contactMapsUrl }: { contactMapsUrl: string | undefined }) {
  if (!contactMapsUrl) return null
  return (
    <div className="contact-map" style={{ marginTop: '1.5rem' }}>
      <img src="/images/microsite/map-placeholder.jpg" alt="Map" style={{ width: '100%', height: 300, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
    </div>
  )
}

function RoomAmenities({ roomAmenities }: { roomAmenities: string[] }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
      {roomAmenities.map((a) => (
        <span key={a} className="amenity-tag">{a}</span>
      ))}
    </div>
  )
}

interface OtherRoomsListProps {
  availableRooms: Array<{ name: string; size_sqm?: string | number; price?: string | number }>
  labelAvailableRooms: string
  labelSqm: string
  labelPerMonth: string
}

function OtherRoomsList({ availableRooms, labelAvailableRooms, labelSqm, labelPerMonth }: OtherRoomsListProps) {
  if (availableRooms.length === 0) return null
  return (
    <div style={{ marginTop: '2rem' }}>
      <p className="section-title">{labelAvailableRooms}</p>
      <div className="other-rooms-list">
        {availableRooms.map((rm, i) => (
          <div key={i} className="other-room-item">
            <div className="other-room-info">
              <p className="other-room-name">{rm.name}</p>
              <p className="other-room-size">{rm.size_sqm} {labelSqm}</p>
            </div>
            <p className="other-room-price">฿ {rm.price}{labelPerMonth}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

interface LandingPageProps {
  heroImg: string
  heroTitle: string
  heroSubtitle: string
  heroCta: string
  homeRooms: Array<{ name?: string; price?: string | number; image?: string; cta?: { text?: string } }>
  labelFeaturedRooms: string
  labelPerMonth: string
  labelDetails: string
}

function LandingPage({ heroImg, heroTitle, heroSubtitle, heroCta, homeRooms, labelFeaturedRooms, labelPerMonth, labelDetails }: LandingPageProps) {
  return (
    <>
      <section className="hero hero--modern">
        <div className="hero__bg" style={{ backgroundImage: `url(${heroImg})` }} />
        <div className="hero__overlay" />
        <div className="container hero__content">
          <h1 className="hero__title">{heroTitle}</h1>
          <p className="hero__tagline">{heroSubtitle}</p>
          {heroCta && <a href="#" className="btn btn--primary" onClick={(e) => e.preventDefault()}>{heroCta}</a>}
        </div>
      </section>
      <section id="featured-rooms" className="home-rooms section" style={{ flex: 1 }}>
        <div className="container">
          <p className="section-title">{labelFeaturedRooms}</p>
          <div className="rooms-grid">
            {homeRooms.map((rm, i) => {
              const name = rm.name || ''
              const price = rm.price ? `฿ ${rm.price}` : ''
              const img = rm.image
              return (
                <div key={i} className="room-card">
                  {img
                    ? <img src={img} alt={name} className="room-card__image" />
                    : <div className="room-card__image" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f1f5f9' }}><span style={{ fontSize: '2rem', opacity: 0.3 }}>📷</span></div>
                  }
                  <div className="room-card__body">
                    <p className="room-card__name">{name}</p>
                    {price && (
                      <p className="room-card__price">
                        <span className="price-value">{price}</span>{labelPerMonth}
                      </p>
                    )}
                    {(rm.cta?.text || labelDetails) && (
                      <a href="#" className="btn btn--primary btn--sm" onClick={(e) => e.preventDefault()} style={{ marginTop: '0.75rem', display: 'inline-block' }}>{rm.cta?.text || labelDetails}</a>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}

interface RoomsPageProps {
  roomImages: string[]
  roomName: string
  roomPrice: string
  roomSize: string | number
  roomDesc: string
  roomAmenities: string[]
  availableRooms: Array<{ name: string; size_sqm?: string | number; price?: string | number }>
  labelPerMonth: string
  labelSqm: string
  labelInquire: string
  labelAvailableRooms: string
}

function RoomsPage({ roomImages, roomName, roomPrice, roomSize, roomDesc, roomAmenities, availableRooms, labelPerMonth, labelSqm, labelInquire, labelAvailableRooms }: RoomsPageProps) {
  return (
    <div className="content-page section">
      <div className="container">
        {roomImages[0] && (
          <img src={roomImages[0]} alt={roomName} className="room-card__image" style={{ borderRadius: 12, marginBottom: '1.5rem', height: 320, objectFit: 'cover', width: '100%' }} />
        )}
        <h1 className="page-title">{roomName}</h1>
        {roomPrice && (
          <p className="room-card__price" style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            <span className="price-value">{roomPrice}</span>
            <span style={{ fontSize: '1rem', fontWeight: 400 }}>{labelPerMonth}</span>
          </p>
        )}
        {roomSize && <p style={{ color: '#64748b', marginBottom: '0.5rem' }}>{roomSize} {labelSqm}</p>}
        {roomDesc && <p style={{ color: '#475569', marginBottom: '1rem', lineHeight: 1.6 }}>{roomDesc}</p>}
        <RoomAmenities roomAmenities={roomAmenities} />
        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <a href="#" className="btn btn--primary" onClick={(e) => e.preventDefault()}>{labelInquire}</a>
        </div>
        <OtherRoomsList availableRooms={availableRooms} labelAvailableRooms={labelAvailableRooms} labelSqm={labelSqm} labelPerMonth={labelPerMonth} />
      </div>
    </div>
  )
}

interface ContactPageProps {
  contactHeading: string
  contactDesc: string | undefined
  contactAddress: string | undefined
  contactPhone: string | undefined
  contactEmail: string | undefined
  contactMapsUrl: string | undefined
  labelInquire: string
}

function ContactPage({ contactHeading, contactDesc, contactAddress, contactPhone, contactEmail, contactMapsUrl, labelInquire }: ContactPageProps) {
  return (
    <div className="content-page section">
      <div className="container">
        <h1 className="page-title">{contactHeading}</h1>
        {contactDesc && <p style={{ color: '#475569', marginBottom: '1.5rem', lineHeight: 1.6 }}>{contactDesc}</p>}
        <ContactDetails contactAddress={contactAddress} contactPhone={contactPhone} contactEmail={contactEmail} />
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <a href="#" className="btn btn--primary" onClick={(e) => e.preventDefault()}>{labelInquire}</a>
        </div>
        <MapBox contactMapsUrl={contactMapsUrl} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ThemePreview — exported so MicrositePageList can import it
// ---------------------------------------------------------------------------

export function ThemePreview({ theme, template, propertyName, lockedPage, pageContent, sitePages }: { theme: MicrositeTheme; template: Template | null; propertyName: string; lockedPage?: PreviewPage; pageContent?: import('@/hooks/useMicrosite').MicrositePageContent; sitePages?: import('@/hooks/useMicrosite').MicrositePage[] }) {
  const { t } = useTranslation('microsite')
  const [previewPage, setPreviewPage] = useState<PreviewPage>(lockedPage || 'landing')
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('desktop')
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(0)

  // Sync when lockedPage changes
  useEffect(() => {
    if (lockedPage) queueMicrotask(() => setPreviewPage(lockedPage))
  }, [lockedPage])

  // Measure container width to calculate scale dynamically
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const tmplKey = template || 'modern'
  useMicrositeCSS(tmplKey)

  const preset = TEMPLATE_PRESETS[tmplKey]
  const p = theme.primary_color || preset.theme.primary_color!
  const s = theme.secondary_color || preset.theme.secondary_color!
  const acc = theme.accent_color || preset.theme.accent_color!
  const btn = theme.button_color || preset.theme.button_color!
  const btnTx = theme.button_text_color || preset.theme.button_text_color!
  const hdrBg = theme.header_bg_color || preset.theme.header_bg_color!
  const hdrTx = theme.header_text_color || preset.theme.header_text_color!
  const ftrBg = theme.footer_bg_color || preset.theme.footer_bg_color!
  const ftrTx = theme.footer_text_color || preset.theme.footer_text_color!
  const bFont = theme.font_family || preset.theme.font_family!
  const hFont = theme.heading_font_family || preset.theme.heading_font_family || bFont
  useGoogleFonts([bFont, hFont])
  const mob = previewDevice === 'mobile'
  const heroImg = theme.hero_image_url || HERO_IMAGES[tmplKey]

  // Scaling: render full-width content then scale to fit the container
  const innerWidth = mob ? 390 : 1200
  const scale = containerWidth > 0 ? containerWidth / innerWidth : 0.35
  const innerHeight = 1600 // approximate full-page height

  // Resolve page content: use pageContent override for locked page, otherwise from pages array
  const pageTypeMap: Record<PreviewPage, string> = { landing: 'home', rooms: 'room', contact: 'contact' }
  const resolveContent = (page: PreviewPage) => {
    if (lockedPage === page && pageContent) return pageContent
    const found = sitePages?.find((pg) => pg.page_type === pageTypeMap[page])
    return found?.content ?? {}
  }
  const hc = resolveContent('landing') as import('@/hooks/useMicrosite').HomePageContent
  const rc = resolveContent('rooms') as import('@/hooks/useMicrosite').RoomPageContent
  const cc = resolveContent('contact') as import('@/hooks/useMicrosite').ContactPageContent

  const heroTitle = hc.hero_title || ''
  const heroSubtitle = hc.hero_subtitle || ''
  const heroCta = hc.hero_cta?.text || ''
  const homeRooms = hc.rooms ?? []
  const roomName = rc.room_name || ''
  const roomPrice = rc.price ? `฿ ${rc.price}` : ''
  const roomSize = rc.size_sqm || ''
  const roomDesc = rc.description || ''
  const roomAmenities = rc.amenities ?? []
  const roomImages = rc.images ?? []
  const availableRooms = rc.available_rooms ?? []
  const contactHeading = cc.heading || ''
  const contactDesc = cc.description
  const contactPhone = cc.phone
  const contactEmail = cc.email
  const contactAddress = cc.address
  const contactMapsUrl = cc.google_maps_embed_url

  const nav = { landing: t('preview.nav_home'), rooms: t('preview.nav_rooms'), contact: t('preview.nav_contact') }

  // CSS custom properties injected on the wrapper — all classes in modern.css read these
  const cssVars = {
    '--primary-color': p,
    '--secondary-color': s,
    '--accent-color': acc,
    '--button-color': btn,
    '--button-text-color': btnTx,
    '--header-bg-color': hdrBg,
    '--header-text-color': hdrTx,
    '--footer-bg-color': ftrBg,
    '--footer-text-color': ftrTx,
    '--font-family': `'${bFont}', sans-serif`,
    '--heading-font-family': `'${hFont}', sans-serif`,
  } as React.CSSProperties

  const [navOpen, setNavOpen] = useState(false)
  useEffect(() => {
    queueMicrotask(() => setNavOpen(false))
  }, [previewDevice])

  const sharedHeaderProps: PreviewHeaderProps = { logoUrl: theme.logo_url, propertyName, nav, navOpen, setNavOpen, previewPage }
  const sharedFooterProps: PreviewFooterProps = { propertyName, footerText: theme.footer_text, contactPhone: theme.contact_phone, contactEmail: theme.contact_email, nav }

  const pageProps = {
    landing: { heroImg, heroTitle, heroSubtitle, heroCta, homeRooms, labelFeaturedRooms: t('preview.featured_rooms'), labelPerMonth: t('preview.per_month'), labelDetails: t('preview.details') } satisfies LandingPageProps,
    rooms: { roomImages, roomName, roomPrice, roomSize, roomDesc, roomAmenities, availableRooms, labelPerMonth: t('preview.per_month'), labelSqm: t('preview.sqm'), labelInquire: t('preview.inquire'), labelAvailableRooms: t('preview.available_rooms') } satisfies RoomsPageProps,
    contact: { contactHeading, contactDesc, contactAddress, contactPhone, contactEmail, contactMapsUrl, labelInquire: t('preview.inquire') } satisfies ContactPageProps,
  }

  const pageComponents = { landing: LandingPage, rooms: RoomsPage, contact: ContactPage }
  const Page = pageComponents[previewPage]
  const currentPageProps = pageProps[previewPage]

  return (
    <Card>
      {/* Controls row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wider">{t('preview.title')}</h3>
        <div className="flex items-center border border-base-300 rounded-lg overflow-hidden">
          <button className={`px-2.5 py-1 text-xs transition-colors ${previewDevice === 'desktop' ? 'bg-primary text-primary-content' : 'bg-base-100 text-base-content/60 hover:bg-base-200'}`}
            onClick={() => setPreviewDevice('desktop')}>{t('preview.desktop')}</button>
          <button className={`px-2.5 py-1 text-xs transition-colors ${previewDevice === 'mobile' ? 'bg-primary text-primary-content' : 'bg-base-100 text-base-content/60 hover:bg-base-200'}`}
            onClick={() => setPreviewDevice('mobile')}>{t('preview.mobile')}</button>
        </div>
      </div>

      {/* Page nav tabs */}
      {!lockedPage && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {(['landing', 'rooms', 'contact'] as const).map((pg) => (
            <button key={pg} className="text-xs font-medium transition-colors" style={{
              padding: '5px 14px', borderRadius: 9999,
              backgroundColor: previewPage === pg ? p : 'transparent',
              color: previewPage === pg ? '#fff' : 'var(--color-base-content)',
              opacity: previewPage === pg ? 1 : 0.5,
            }} onClick={() => setPreviewPage(pg)}>{nav[pg]}</button>
          ))}
        </div>
      )}

      {/* Width measurement target — stable, always 100% */}
      <div ref={containerRef} style={{ width: '100%' }}>
        {mob && containerWidth > 0 && containerWidth < 390 ? (
          <div style={{ padding: '3rem 1rem', textAlign: 'center', color: 'var(--color-base-content)', opacity: 0.5 }}>
            <p className="text-sm">{t('preview.desktop')}: {Math.round(containerWidth)}px</p>
            <p className="text-xs mt-1">Screen too narrow for mobile preview. Zoom out or widen your browser.</p>
          </div>
        ) : mob ? (
          /* Mobile: no transform — render at natural width so sticky + scroll work.
             @container query triggers at ≤768px for hamburger, single-col grid, etc. */
          <div
            className={`${tmplKey}-template`}
            style={{
              ...cssVars,
              width: 390,
              margin: '0 auto',
              borderRadius: 10,
              border: '1px solid var(--color-base-300)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            {/* Scroll wrapper inside container so sticky header works */}
            <div style={{ maxHeight: 500, overflowY: 'auto' }}>
              <PreviewHeader {...sharedHeaderProps} />
              <main className="main-content">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Page {...(currentPageProps as any)} />
              </main>
              <PreviewFooter {...sharedFooterProps} />
            </div>
          </div>
        ) : (
          /* Desktop: scale 1200px → container width via transform */
          <div style={{
            width: '100%',
            height: Math.round(innerHeight * scale),
            overflow: 'hidden',
            borderRadius: 10,
            border: '1px solid var(--color-base-300)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}>
            <div
              className={`${tmplKey}-template`}
              style={{
                ...cssVars,
                width: innerWidth,
                minHeight: innerHeight,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column' as const,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <PreviewHeader {...sharedHeaderProps} />
              <main className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <Page {...(currentPageProps as any)} />
              </main>
              <PreviewFooter {...sharedFooterProps} />
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Image upload helper
// ---------------------------------------------------------------------------

async function uploadMicrositeImage(file: File): Promise<string> {
  const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
    content_type: file.type,
  })
  await fetch(presigned.url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } })
  return `/api/images/${presigned.key}`
}

// ---------------------------------------------------------------------------
// PropertyMicrositeFields — main export
// ---------------------------------------------------------------------------

export interface PropertyMicrositeFieldsProps {
  property: Property
  /**
   * Optional ref that PropertyDetailPage wires to trigger a save externally.
   * When provided, the ref.current is set to the component's handleSaveMicrosite
   * function so the page-header Save button can call ref.current() directly.
   */
  saveRef?: RefObject<(() => Promise<void>) | null>
  /**
   * Optional callback fired whenever this component's dirty state changes.
   * Used by PropertyDetailPage to aggregate per-tab dirty state for the
   * global Save button and dirty-dot badges.
   */
  onDirtyChange?: (isDirty: boolean) => void
  className?: string
}

export function PropertyMicrositeFields({ property, saveRef, onDirtyChange }: PropertyMicrositeFieldsProps) {
  const { t: tm } = useTranslation('microsite')
  const propertyId = property.id

  const { data: microsite } = useMicrosite(propertyId)
  const { data: micrositePages } = useMicrositePages(propertyId)
  const updateMicrosite = useUpdateMicrosite()
  const publishMicrosite = usePublishMicrosite()

  const [slug, setSlug] = useState('')
  const [slugError, setSlugError] = useState('')
  const [template, setTemplate] = useState<Template | null>(null)
  const [theme, setTheme] = useState<MicrositeTheme>({})
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle')

  // Populate form from fetched microsite data
  useEffect(() => {
    if (microsite) {
      queueMicrotask(() => {
        setSlug(microsite.microsite_slug ?? '')
        setTemplate((microsite.microsite_template as Template) ?? null)
        // When no theme exists yet, pre-fill with modern template defaults + contact defaults
        if (!microsite.microsite_theme || Object.keys(microsite.microsite_theme).length === 0) {
          const defaultPreset = TEMPLATE_PRESETS.modern
          setTheme({
            ...defaultPreset.theme,
            footer_text: `© ${new Date().getFullYear()} ${property.name}`,
            contact_phone: '02-xxx-xxxx',
            contact_email: 'contact@example.com',
            contact_address: '123 Sukhumvit Road, Bangkok 10110',
          })
          if (!microsite.microsite_template) setTemplate('modern')
        } else {
          setTheme(microsite.microsite_theme)
        }
      })
    }
  }, [microsite, property.name])

  const slugify = useCallback((text: string): string => {
    return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '')
  }, [])

  useEffect(() => {
    if (property && !microsite?.microsite_slug) {
      queueMicrotask(() => setSlug(slugify(property.name)))
    }
  }, [property, microsite?.microsite_slug, slugify])

  const handleSlugChange = (value: string) => {
    const cleaned = value.toLowerCase().replace(/\s+/g, '-')
    setSlug(cleaned)
    if (cleaned && !SLUG_REGEX.test(cleaned)) setSlugError(tm('slug.error_format'))
    else if (!cleaned) setSlugError(tm('slug.error_required'))
    else setSlugError('')
  }

  const updateTheme = useCallback((key: keyof MicrositeTheme, value: string) => {
    setTheme((prev) => ({ ...prev, [key]: value }))
  }, [])

  const handleSaveMicrosite = async () => {
    if (!slug) { setSlugError(tm('slug.error_required')); return }
    if (!SLUG_REGEX.test(slug)) { setSlugError(tm('slug.error_format')); return }
    try {
      setSaveStatus('saving')
      await updateMicrosite.mutateAsync({
        propertyId, microsite_slug: slug, microsite_template: template ?? undefined, microsite_theme: theme,
      })
      setSaveStatus('success')
      setTimeout(() => setSaveStatus('idle'), 3000)
    } catch {
      setSaveStatus('error')
      setTimeout(() => setSaveStatus('idle'), 3000)
    }
  }

  // Expose handleSaveMicrosite via saveRef so the page-header Save button can
  // trigger this component's save without being inside the component's DOM subtree.
  useEffect(() => {
    if (saveRef) {
      saveRef.current = handleSaveMicrosite
    }
    return () => {
      if (saveRef) {
        saveRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRef, slug, template, theme])

  // Compute dirty state by comparing current state to the fetched microsite data.
  // Reports true as soon as the user changes slug, template, or any theme field.
  useEffect(() => {
    if (!onDirtyChange) return
    if (!microsite) {
      // No microsite data yet — if any state has been set by the user it's dirty
      onDirtyChange(slug !== '' || template !== null || Object.keys(theme).length > 0)
      return
    }
    const originalSlug = microsite.microsite_slug ?? ''
    const originalTemplate = (microsite.microsite_template as string) ?? null
    const originalTheme = JSON.stringify(microsite.microsite_theme ?? {})
    const currentTheme = JSON.stringify(theme)
    const dirty =
      slug !== originalSlug || template !== originalTemplate || currentTheme !== originalTheme
    onDirtyChange(dirty)
  }, [onDirtyChange, microsite, slug, template, theme])

  const handlePublish = async () => {
    try {
      // Auto-save before publishing so the latest theme/slug is persisted
      await handleSaveMicrosite()
      await publishMicrosite.mutateAsync(propertyId)
    } catch {
      // error handled by API status
    }
  }

  const siteUrl = slug ? `${slug}.flowrent.io` : null

  return (
    <div className="space-y-6">
      {/* Action bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {microsite?.microsite_publish_status === 'publishing' && (
            <Badge variant="warning">{tm('card.status_publishing')}</Badge>
          )}
          {microsite?.microsite_publish_status === 'published' && (
            <Badge variant="success">{tm('card.status_published')}</Badge>
          )}
          {microsite?.microsite_publish_status === 'failed' && (
            <Badge variant="error">{tm('card.status_failed')}</Badge>
          )}
          {siteUrl && microsite?.microsite_published_at && (
            <a href={`https://${siteUrl}`} target="_blank" rel="noopener noreferrer" className="link link-primary text-sm font-mono">
              {siteUrl}
            </a>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={handlePublish} loading={publishMicrosite.isPending || microsite?.microsite_publish_status === 'publishing'} disabled={microsite?.microsite_publish_status === 'publishing'}>
            {microsite?.microsite_publish_status === 'publishing' ? tm('actions.publishing') : tm('actions.publish')}
          </Button>
          <Button type="button" onClick={handleSaveMicrosite} loading={saveStatus === 'saving'}>
            {tm('actions.save')}
          </Button>
        </div>
      </div>

      {saveStatus === 'success' && <Alert variant="success" icon={null} role="status" className="text-sm"><span>{tm('messages.save_success')}</span></Alert>}
      {saveStatus === 'error' && <Alert variant="error" icon={null} role="alert" className="text-sm"><span>{tm('messages.save_error')}</span></Alert>}
      {microsite?.microsite_publish_status === 'failed' && <Alert variant="error" icon={null} role="alert" className="text-sm"><span>{tm('messages.publish_error')}{microsite.microsite_publish_error ? `: ${microsite.microsite_publish_error}` : ''}</span></Alert>}

      {/* Two-column: Settings + Live Preview */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left: Settings */}
        <div className="lg:col-span-2 space-y-6">
          {/* Slug — only show input when not yet set */}
          {!microsite?.microsite_slug && (
            <Card title={tm('slug.label')}>
              <div className="space-y-4">
                <div>
                  <Input label={tm('slug.label')} placeholder={tm('slug.placeholder')} value={slug} onChange={(e) => handleSlugChange(e.target.value)} error={slugError} />
                  <p className="mt-1 text-xs text-base-content/60">{tm('slug.hint')}</p>
                </div>
              </div>
            </Card>
          )}

          {/* Template — grid wraps to 2 columns on mobile (grid-cols-2), 3 columns on sm+ */}
          <Card title={tm('template.label')}>
            <p className="text-sm text-base-content/60 mb-4">{tm('template.description')}</p>
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3">
              {TEMPLATES.map((tmpl) => {
                const isSelected = template === tmpl
                const tp = TEMPLATE_PRESETS[tmpl]
                const tpTheme = tp.theme
                return (
                  <button
                    key={tmpl}
                    type="button"
                    onClick={() => {
                      setTemplate(tmpl)
                      setTheme((prev) => ({
                        ...prev,
                        ...tp.theme,
                        footer_text: prev.footer_text || `© ${new Date().getFullYear()} ${property.name}`,
                        contact_phone: prev.contact_phone || '02-xxx-xxxx',
                        contact_email: prev.contact_email || 'contact@example.com',
                        contact_address: prev.contact_address || '123 Sukhumvit Road, Bangkok 10110',
                      }))
                    }}
                    className={[
                      'relative flex flex-col overflow-hidden text-left transition-all',
                      isSelected ? 'ring-2 ring-primary/50' : 'hover:ring-1 hover:ring-primary/30',
                    ].join(' ')}
                    style={{ borderRadius: tp.cardR, border: isSelected ? `2px solid ${tpTheme.primary_color}` : '2px solid var(--color-base-300)' }}
                    aria-pressed={isSelected}
                  >
                    {/* Mini hero preview using template colors */}
                    <div
                      className="h-20 w-full relative flex flex-col items-center justify-center gap-1"
                      style={{
                        background: tmpl === 'classic'
                          ? `linear-gradient(135deg, ${tpTheme.accent_color} 0%, #f5e6d3 100%)`
                          : `linear-gradient(135deg, ${tpTheme.primary_color} 0%, ${tpTheme.secondary_color} 100%)`,
                      }}
                    >
                      <span className="text-[10px] font-bold" style={{ color: tp.heroTextLight ? '#fff' : tpTheme.primary_color, fontFamily: tpTheme.heading_font_family }}>
                        {tm(`template.${tmpl}_name`)}
                      </span>
                      <span
                        className="text-[7px] px-2 py-0.5 font-medium"
                        style={{ backgroundColor: tpTheme.button_color, color: tpTheme.button_text_color, borderRadius: tp.btnR, border: tp.btnBorder(tpTheme.button_color!) }}
                      >
                        {tm('preview.hero_cta')}
                      </span>
                    </div>
                    {/* Bottom bar using header/footer colors */}
                    <div className="px-3 py-2 flex items-center justify-between" style={{ backgroundColor: tpTheme.header_bg_color, color: tpTheme.header_text_color }}>
                      <span className="text-[9px] font-semibold" style={{ fontFamily: tpTheme.heading_font_family }}>{tm(`template.${tmpl}_name`)}</span>
                      {isSelected && <Check size={14} style={{ color: tpTheme.accent_color }} />}
                    </div>
                    <div className="px-3 py-1.5">
                      <p className="text-[10px] text-base-content/60" style={{ fontFamily: tpTheme.font_family }}>{tm(`template.${tmpl}_description`)}</p>
                    </div>
                  </button>
                )
              })}
            </div>
          </Card>

          {/* Colors */}
          <Card title={tm('theme.section_colors')}>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <ColorPicker label={tm('theme.primary_color')} value={theme.primary_color ?? ''} onChange={(v) => updateTheme('primary_color', v)} />
              <ColorPicker label={tm('theme.secondary_color')} value={theme.secondary_color ?? ''} onChange={(v) => updateTheme('secondary_color', v)} />
              <ColorPicker label={tm('theme.accent_color')} value={theme.accent_color ?? ''} onChange={(v) => updateTheme('accent_color', v)} />
              <ColorPicker label={tm('theme.button_color')} value={theme.button_color ?? ''} onChange={(v) => updateTheme('button_color', v)} />
              <ColorPicker label={tm('theme.button_text_color')} value={theme.button_text_color ?? ''} onChange={(v) => updateTheme('button_text_color', v)} />
              <ColorPicker label={tm('theme.header_bg_color')} value={theme.header_bg_color ?? ''} onChange={(v) => updateTheme('header_bg_color', v)} />
              <ColorPicker label={tm('theme.header_text_color')} value={theme.header_text_color ?? ''} onChange={(v) => updateTheme('header_text_color', v)} />
              <ColorPicker label={tm('theme.footer_bg_color')} value={theme.footer_bg_color ?? ''} onChange={(v) => updateTheme('footer_bg_color', v)} />
              <ColorPicker label={tm('theme.footer_text_color')} value={theme.footer_text_color ?? ''} onChange={(v) => updateTheme('footer_text_color', v)} />
            </div>
          </Card>

          {/* Fonts */}
          <Card title={tm('theme.section_fonts')}>
            <div className="grid gap-4 sm:grid-cols-2">
              <fieldset className="fieldset">
                <legend className="fieldset-legend">{tm('theme.font_family')}</legend>
                <FontFamilySelect
                  value={theme.font_family ?? ''}
                  onChange={(e) => updateTheme('font_family', e.target.value)}
                  label={tm('theme.font_family')}
                />
              </fieldset>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">{tm('theme.heading_font_family')}</legend>
                <FontFamilySelect
                  value={theme.heading_font_family ?? ''}
                  onChange={(e) => updateTheme('heading_font_family', e.target.value)}
                  label={tm('theme.heading_font_family')}
                />
              </fieldset>
            </div>
          </Card>

          {/* Images */}
          <Card title={tm('theme.section_images')}>
            <div className="space-y-6">
              <FileUpload label={tm('theme.logo_url')} hint={tm('theme.logo_hint')} value={theme.logo_url ? [theme.logo_url] : []} onChange={(urls) => updateTheme('logo_url', urls[0] ?? '')} maxFiles={1} accept="image/*" onUpload={uploadMicrositeImage} />
              {/* Hero image — full-width on all viewports */}
              <div className="w-full">
                <FileUpload label={tm('theme.hero_image_url')} hint={tm('theme.hero_image_hint')} value={theme.hero_image_url ? [theme.hero_image_url] : []} onChange={(urls) => updateTheme('hero_image_url', urls[0] ?? '')} maxFiles={1} accept="image/*" onUpload={uploadMicrositeImage} />
              </div>
              <FileUpload label={tm('theme.favicon_url')} hint={tm('theme.favicon_hint')} value={theme.favicon_url ? [theme.favicon_url] : []} onChange={(urls) => updateTheme('favicon_url', urls[0] ?? '')} maxFiles={1} accept="image/*,.ico" onUpload={uploadMicrositeImage} />
            </div>
          </Card>

          {/* Footer */}
          <Card title={tm('theme.section_footer')}>
            <div className="space-y-4">
              <Textarea label={tm('theme.footer_text')} placeholder={tm('theme.footer_text_placeholder')} rows={2} value={theme.footer_text ?? ''} onChange={(e) => updateTheme('footer_text', e.target.value)} />
            </div>
          </Card>

          {/* SEO / Social Sharing */}
          <Card title={tm('theme.seo.title')}>
            <div className="space-y-4">
              <div>
                <Textarea label={tm('theme.seo.meta_description')} placeholder={tm('theme.seo.meta_description_placeholder')} rows={2} maxLength={160} value={theme.meta_description ?? ''} onChange={(e) => updateTheme('meta_description', e.target.value)} />
                <span className="text-xs text-base-content/50 mt-1 block">{(theme.meta_description ?? '').length}/160</span>
              </div>
              <Input label={tm('theme.seo.og_title')} type="text" placeholder={tm('theme.seo.og_title_placeholder')} value={theme.og_title ?? ''} onChange={(e) => updateTheme('og_title', e.target.value)} />
              <Textarea label={tm('theme.seo.og_description')} placeholder={tm('theme.seo.og_description_placeholder')} rows={3} value={theme.og_description ?? ''} onChange={(e) => updateTheme('og_description', e.target.value)} />
              <FileUpload label={tm('theme.seo.og_image')} hint={tm('theme.seo.og_image_hint')} value={theme.og_image ? [theme.og_image] : []} onChange={(urls) => updateTheme('og_image', urls[0] ?? '')} maxFiles={1} accept="image/*" onUpload={uploadMicrositeImage} />
            </div>
          </Card>

          {/* Analytics & Tracking */}
          <Card title={tm('theme.analytics.title')}>
            <div className="space-y-4">
              <p className="text-xs text-base-content/50">{tm('theme.analytics.hint')}</p>
              <Input label={tm('theme.analytics.gtm_id')} type="text" size="sm" placeholder={tm('theme.analytics.gtm_id_placeholder')} value={theme.gtm_id ?? ''} onChange={(e) => updateTheme('gtm_id', e.target.value)} />
              <Input label={tm('theme.analytics.ga4_id')} type="text" size="sm" placeholder={tm('theme.analytics.ga4_id_placeholder')} value={theme.ga4_id ?? ''} onChange={(e) => updateTheme('ga4_id', e.target.value)} />
              <Input label={tm('theme.analytics.clarity_id')} type="text" size="sm" placeholder={tm('theme.analytics.clarity_id_placeholder')} value={theme.clarity_id ?? ''} onChange={(e) => updateTheme('clarity_id', e.target.value)} />
            </div>
          </Card>

          {/* Pages */}
          <MicrositePageList propertyId={propertyId} theme={theme} template={template} propertyName={property.name} />
        </div>

        {/* Right: Live Theme Preview (sticky) */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4">
            <ThemePreview theme={theme} template={template} propertyName={property.name} sitePages={micrositePages} />
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {saveStatus === 'success' && <span className="text-sm text-success">{tm('messages.save_success')}</span>}
        {saveStatus === 'error' && <span className="text-sm text-error">{tm('messages.save_error')}</span>}
        <Button type="button" onClick={handleSaveMicrosite} loading={saveStatus === 'saving'}>
          {tm('actions.save')}
        </Button>
      </div>
    </div>
  )
}
